import Logger from '../Logger.js';
import BaseEvent from './BaseEvent.js';
import MediaStreamBuilder from '../MediaStreamBuilder.js';
import { getScreenPresentationTracks } from '../utils/StreamHelpers.js';
import MicMixer from '../MicMixer.js';

const destroySessionMicMixer = session => {
  if (session.micMixer) {
    session.micMixer.destroy();
    session.micMixer = null;
  }
};

const getScreenErrorName = error => {
  let name = `error_Screen_${error.name}`;
  if (error.message.indexOf('not start audio') > -1) {
    name = `error_Screen_${error.name}_Audio`;
  }
  return name;
};

const detectDisplaySurface = screenTrack => {
  if (screenTrack && typeof screenTrack.getSettings === 'function') {
    const settings = screenTrack.getSettings();
    if (
      Reflect.apply(Object.prototype.hasOwnProperty, settings, [
        'displaySurface'
      ])
    ) {
      return settings.displaySurface;
    }
  }
  return '';
};

class ScreenCaptureChangeEvent extends BaseEvent {
  async handle({ surface }) {
    let screenStream;
    const { _session } = this.context;
    if (!_session) {
      throw new Error('Session does not exist');
    }
    if (_session.options.recvOnly) {
      return;
    }
    try {
      const mediaStreamBuilder = new MediaStreamBuilder({ surface });
      screenStream = await mediaStreamBuilder.getDisplayMedia();
    } catch (error) {
      Logger.error('ScreenCaptureChangeEvent', error);
      _session.emit({ type: 'warning', name: getScreenErrorName(error) });
      return;
    }
    destroySessionMicMixer(_session);
    _session.micMixer = new MicMixer();
    const options = {
      audio: _session.localStream.getAudioTracks()[0].enabled,
      video: false,
      screen: true,
      surface,
      micMixer: _session.micMixer,
      screenStream,
      isPresentation: true,
      deviceMonitor: _session.deviceMonitor
    };

    try {
      let present = 'screen';
      const stream = await new MediaStreamBuilder(options).start();
      const [screenTrack] = getScreenPresentationTracks(stream);
      if (screenTrack) {
        screenTrack.addEventListener('ended', () => {
          _session.emit({ type: 'stop_presenting' });
        });
        const displaySurface = detectDisplaySurface(screenTrack);
        if (displaySurface) {
          present = `screen-${displaySurface}`;
        }
      }
      _session.setStream(stream);
      _session.emit({ type: 'presentation_changed', present });
    } catch (error) {
      Logger.error('ScreenCaptureChangeEvent', error);
      destroySessionMicMixer(_session);
      let name = `error_Screen_${error.name}`;
      if (error.message.indexOf('not start audio') > -1) {
        name = `error_Screen_${error.name}_Audio`;
      }
      _session.emit({ type: 'capture_error', name });
      _session.emit({ type: 'stop_presenting' });
    }
  }
}

export default ScreenCaptureChangeEvent;
